import { RefObject, JSX } from 'react';
import { Add, Delete } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Grid, IconButton, Typography } from '@mui/material';
import { BackstoryQuery } from 'components/BackstoryQuery';
import { ConversationHandle } from 'components/Conversation';
import { TransientChat } from 'components/ui/TransientChat';
import * as Types from 'types/types';

interface ProfileQuestionsTabProps {
  questions?: Types.CandidateQuestion[];
  isMobile: boolean;
  chatRef: RefObject<ConversationHandle | null>;
  onAddQuestion: () => void;
  onRemoveQuestion: (index: number) => void;
}

function ProfileQuestionsTab({
  questions = [],
  isMobile,
  chatRef,
  onAddQuestion,
  onRemoveQuestion,
}: ProfileQuestionsTabProps): JSX.Element {
  const handleSubmitQuestion = (question: Types.CandidateQuestion): void => {
    chatRef.current?.submitQuery({ prompt: question.question });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Questions for Chat</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={onAddQuestion}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Question
        </Button>
      </Box>

      <Box>See how Backstory answers the question about you...</Box>
      <TransientChat id={Date()} ref={chatRef} />

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ maxWidth: '100%' }}>
        {questions.map((question, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${question.question}-${index}`}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <BackstoryQuery question={question} submitQuery={handleSubmitQuestion} />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(): void => onRemoveQuestion(index)}
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {questions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No questions added yet. Click &quot;Add Question&quot; to get started.
        </Typography>
      )}
    </Box>
  );
}

export { ProfileQuestionsTab };
